import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Database, Compass, Settings, LogOut, Sparkles, ShieldCheck, Moon, Sun, BellRing, HelpCircle, ScrollText, Gem, FolderKanban, Megaphone, Eye, Home, Building2, LogOut as LogOutIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { NotificationBell } from "@/components/bi/NotificationBell";
import { TenantSwitcher } from "@/components/bi/TenantSwitcher";
import { cn } from "@/lib/utils";
import { roleLabel, isTenantAdmin, isCrossTenant } from "@/lib/roles";
import api from "@/lib/api";
import { usePlatformSettings } from "@/hooks/usePlatformSettings";
import { useRegional } from "@/context/RegionalContext";
import { t } from "@/lib/i18n";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const NAV = [
  { to: "/home", label: "Home", key: "home", icon: Home, testid: "nav-home" },
  { to: "/", label: "Dashboards", key: "dashboards", icon: LayoutDashboard, testid: "nav-dashboards", end: true },
  { to: "/data", label: "Data Sources", key: "data_sources", icon: Database, testid: "nav-data" },
  { to: "/explore", label: "Explore", key: "explore", icon: Compass, testid: "nav-explore" },
  { to: "/metrics", label: "Metrics", key: "metrics", icon: Gem, testid: "nav-metrics" },
  { to: "/alerts", label: "Alerts", key: "alerts", icon: BellRing, testid: "nav-alerts" },
  { to: "/reports", label: "Reports", key: "reports", icon: ScrollText, testid: "nav-reports" },
  { to: "/eazy", label: "Eazy Console", key: "eazy_console", icon: Building2, testid: "nav-eazy", staffOnly: true },
  { to: "/admin", label: "Admin Console", key: "admin_console", icon: ShieldCheck, testid: "nav-admin", adminOnly: true },
  { to: "/help", label: "Help & Tutorials", key: "help", icon: HelpCircle, testid: "nav-help" },
  { to: "/settings", label: "Settings", key: "settings", icon: Settings, testid: "nav-settings" },
];
const navLabel = (n) => { const s = t(n.key); return s === n.key ? n.label : s; };

export function AppShell({ children, title, actions }) {
  const { user, logout, bootstrap } = useAuth();
  const { branding, theme, setTheme } = useBranding();
  useRegional(); // re-render nav labels when the effective language changes
  const platform = usePlatformSettings();
  const [sites, setSites] = useState(bootstrap?.sites || []);
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  const nav = NAV.filter((n) => (!n.adminOnly || isTenantAdmin(user?.role)) && (!n.staffOnly || isCrossTenant(user?.role)));
  const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");
  useEffect(() => {
    // Prefer sites already delivered by /auth/bootstrap — saves a duplicate GET
    // right after login. Only fall back to /sites if bootstrap resolved with
    // an empty list (e.g. bootstrap partial-failed with a Mongo hiccup);
    // while bootstrap is still `null` (loading) we simply wait.
    if (bootstrap?.sites && bootstrap.sites.length > 0) {
      setSites(bootstrap.sites);
      return;
    }
    if (bootstrap && (!bootstrap.sites || bootstrap.sites.length === 0)) {
      api.get("/sites").then(({ data }) => setSites(Array.isArray(data) ? data : [])).catch(() => setSites([]));
    }
  }, [user?.id, user?.tenant_id, bootstrap]);
  // Only show the shortcuts strip if the tenant actually has more than one site.
  const siteShortcuts = sites.length > 1 ? sites : [];
  return (
    <div className="min-h-screen flex bi-canvas">
      <a href="#main-content" data-testid="skip-to-content" className="skip-link">{t("skip_to_content")}</a>
      {/* Sidebar */}
      <aside aria-label="Primary" data-chrome="sidebar" className="w-[248px] shrink-0 hidden md:flex flex-col border-r border-[color:var(--glass-border)] bg-[color:var(--surface)]/70 backdrop-blur-md sticky top-0 h-screen max-h-screen">
        <div className="px-5 py-5 flex items-center gap-2.5 shrink-0">
          {branding.logo_url ? (
            <img src={branding.logo_url} alt={branding.app_name} className="w-9 h-9 rounded-xl object-cover shadow-[var(--shadow-md)]" />
          ) : (
            <div className="w-9 h-9 rounded-xl bg-[color:var(--brand-ocean)] flex items-center justify-center shadow-[var(--shadow-md)]">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
          )}
          <div className="min-w-0">
            <div data-testid="brand-app-name" className="font-display font-semibold text-[15px] leading-tight text-[color:var(--ink)] truncate">{branding.app_name}</div>
            <div className="text-[11px] text-[color:var(--ink-3)] leading-tight tracking-wide truncate">{branding.tagline}</div>
          </div>
        </div>
        <nav aria-label="Main navigation" className="flex-1 px-3 py-2 space-y-1 overflow-y-auto">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors",
                isActive
                  ? "bg-[color:rgba(43,78,255,0.10)] text-[color:var(--ink)] border-l-2 border-[color:var(--brand-ocean)]"
                  : "text-[color:var(--ink-2)] hover:bg-[color:var(--surface-2)]"
              )}
            >
              <n.icon className="w-[18px] h-[18px]" aria-hidden="true" />
              {navLabel(n)}
            </NavLink>
          ))}
          {siteShortcuts.length > 0 && (
            <div data-testid="nav-sites-section" className="pt-3">
              <div className="px-3 pb-1 flex items-center justify-between">
                <span className="text-[10px] uppercase font-semibold tracking-wider text-[color:var(--ink-3)]">Sites</span>
                <span className="text-[10px] text-[color:var(--ink-3)]">{siteShortcuts.length}</span>
              </div>
              {siteShortcuts.slice(0, 6).map((s) => (
                <NavLink
                  key={s.id}
                  to={`/sites/${s.id}`}
                  data-testid={`nav-site-${s.id}`}
                  title={s.description || s.name}
                  className={({ isActive }) => cn(
                    "flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-colors",
                    isActive
                      ? "bg-[color:rgba(43,78,255,0.10)] text-[color:var(--ink)] border-l-2 border-[color:var(--brand-ocean)]"
                      : "text-[color:var(--ink-2)] hover:bg-[color:var(--surface-2)]"
                  )}
                >
                  <FolderKanban className="w-[16px] h-[16px] shrink-0" />
                  <span className="truncate flex-1">{s.name}</span>
                  {(s.dashboard_count ?? 0) > 0 && (
                    <span className="text-[10px] text-[color:var(--ink-3)] shrink-0">{s.dashboard_count}</span>
                  )}
                </NavLink>
              ))}
              {siteShortcuts.length > 6 && (
                <div className="px-3 py-1 text-[10px] text-[color:var(--ink-3)] italic">+ {siteShortcuts.length - 6} more · manage from Dashboards</div>
              )}
            </div>
          )}
        </nav>
        <div className="p-3">
          <div className="glass-card p-3 flex items-center gap-3">
            <Avatar className="w-9 h-9"><AvatarFallback className="bg-[color:var(--brand-ocean)] text-white text-xs">{initials}</AvatarFallback></Avatar>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-[color:var(--ink)] truncate">{user?.name}</div>
              <div className="text-[11px] text-[color:var(--brand-ocean)] truncate font-medium">{roleLabel(user?.role)}</div>
            </div>
            <button data-testid="sidebar-logout" onClick={logout} aria-label={t("sign_out")} title={t("sign_out")} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)] transition-colors">
              <LogOut className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header data-chrome="header" className="sticky top-0 z-40 backdrop-blur-md bg-[color:var(--glass-bg)] border-b border-[color:var(--glass-border)]">
          <div className="min-h-16 py-2 px-5 md:px-8 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div className="md:hidden w-8 h-8 rounded-lg bg-[color:var(--brand-ocean)] flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" /></div>
              <h1 className="font-display text-xl text-[color:var(--ink)] truncate">{title}</h1>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {isCrossTenant(user?.role) && (
                <span data-testid="tenant-staff-note" title="Everything you do in this tenant is logged as Eazy staff activity"
                  className="hidden lg:inline-flex items-center gap-1 h-9 px-2.5 rounded-xl text-[11px] font-medium text-[color:var(--brand-ocean)] bg-[color:rgba(43,78,255,0.08)] border border-[color:var(--glass-border)]">
                  <ShieldCheck className="w-3.5 h-3.5" />Eazy staff · working in this tenant
                </span>
              )}
              <TenantSwitcher />
              <NotificationBell />
              <button
                data-testid="theme-toggle"
                onClick={toggleTheme}
                aria-label="Toggle theme"
                className="w-9 h-9 rounded-xl flex items-center justify-center text-[color:var(--ink-2)] hover:bg-[color:var(--surface-2)] transition-colors"
              >
                {theme === "dark" ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
              </button>
              {actions}
              <div className="md:hidden">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Avatar className="w-9 h-9 cursor-pointer"><AvatarFallback className="bg-[color:var(--brand-ocean)] text-white text-xs">{initials}</AvatarFallback></Avatar>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout}><LogOut className="w-4 h-4 mr-2" />Log out</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </header>
        {user?.is_impersonation ? (
          <div data-testid="impersonation-banner"
               className="w-full px-5 md:px-8 py-2 text-sm flex items-center gap-3 border-b"
               style={{
                 background: "linear-gradient(90deg, rgba(34,211,238,0.20), rgba(167,139,250,0.10))",
                 borderColor: "rgba(34,211,238,0.45)",
                 color: "#0B0F14",
               }}>
            <Eye className="w-4 h-4 shrink-0" />
            <span className="min-w-0 flex-1 truncate">
              <b>Read-only impersonation.</b> You are viewing this tenant as{" "}
              <span className="font-mono text-[13px]">{user.impersonation_sa_email}</span> — mutations are disabled.
            </span>
            <button
              data-testid="impersonation-end"
              onClick={async () => {
                try { await api.post("/superadmin/impersonate/end"); } catch { /* ignore */ }
                window.location.href = "/superadmin";
              }}
              className="text-xs font-medium px-2.5 py-1 rounded-lg bg-black/10 hover:bg-black/20 border border-black/20 transition shrink-0 flex items-center gap-1.5"
            >
              <LogOutIcon className="w-3.5 h-3.5" />End Session
            </button>
          </div>
        ) : null}
        {platform?.banner_message ? (
          <div data-testid="platform-banner"
               className="w-full px-5 md:px-8 py-2 text-sm flex items-center gap-2 border-b"
               style={{
                 background: "linear-gradient(90deg, rgba(251,191,36,0.14), rgba(251,191,36,0.06))",
                 borderColor: "rgba(251,191,36,0.35)",
                 color: "#B45309",
               }}>
            <Megaphone className="w-4 h-4 shrink-0" />
            <span className="min-w-0 truncate">{platform.banner_message}</span>
          </div>
        ) : null}
        <main id="main-content" tabIndex={-1} className="flex-1 min-h-0 overflow-auto outline-none">{children}</main>
      </div>
    </div>
  );
}
